import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { AnimatedTextField, SegmentControl, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { useStoreActions } from '~/store/store-context';
import styles from './BusinessDetailsBStep.module.scss';
export const GetBusinessDetailsBStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const businessDetailsBStepTextsPath = 'vendorOnboarding.businessDetailsBStep';
const businessDescriptionSuggestionKeys = [
    'suggestionBookkeeping',
    'suggestionTaxAdvisory',
    'suggestionCpaPractice',
    'suggestionFinancialConsultancy'
];
const numInvoicesOptionKeys = [
    'numInvoicesUpTo20',
    'numInvoices20To50',
    'numInvoices50To150',
    'numInvoices150To1000',
    'numInvoices1000Plus'
];
const MIN_DESCRIPTION_LENGTH = 10;
export const BusinessDetailsBStep = (param)=>{
    let { testId, valuesFromStepA, values, onChange, onContinue, submitRef, onSubmittingChange, hasCreatedBusiness, onBusinessCreated, onAuthTokenReceived, lastSubmittedSignature, onSubmitted } = param;
    var _values_numInvoices, _values_teamSize;
    const testIds = useMemo(()=>GetBusinessDetailsBStepTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys(businessDetailsBStepTextsPath);
    const { api } = useStoreActions();
    const [showErrors, setShowErrors] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitFailed, setSubmitFailed] = useState(false);
    const businessDescriptionSuggestions = useMemo(()=>businessDescriptionSuggestionKeys.map((key)=>t[key]()), [
        t
    ]);
    const numInvoicesOptions = useMemo(()=>numInvoicesOptionKeys.map((key)=>({
                value: key,
                label: t[key]()
            })), [
        t
    ]);
    const teamSizeOptions = useMemo(()=>[
            {
                value: 'just_me',
                label: t.teamSizeJustMe()
            },
            {
                value: '2_to_5',
                label: t.teamSize2To5()
            },
            {
                value: '5_to_10',
                label: t.teamSize5To10()
            },
            {
                value: '10_to_50',
                label: t.teamSize10To50()
            },
            {
                value: '50_plus',
                label: t.teamSize50Plus()
            }
        ], [
        t
    ]);
    const errors = useMemo(()=>{
        const result = {};
        if (values.businessDescription.trim().length < MIN_DESCRIPTION_LENGTH) {
            result.businessDescription = t.businessDescriptionRequired();
        }
        if (values.numInvoices == null) {
            result.numInvoices = t.numInvoicesRequired();
        }
        if (values.teamSize == null) {
            result.teamSize = t.teamSizeRequired();
        }
        return result;
    }, [
        values,
        t
    ]);
    useEffect(()=>{
        onSubmittingChange === null || onSubmittingChange === void 0 ? void 0 : onSubmittingChange(isSubmitting);
    }, [
        isSubmitting,
        onSubmittingChange
    ]);
    const handleSubmit = useCallback(async ()=>{
        var _values_numInvoices, _values_teamSize;
        if (isSubmitting) {
            return;
        }
        if (Object.keys(errors).length > 0) {
            setShowErrors(true);
            return;
        }
        const payload = {
            name: valuesFromStepA.companyName,
            description: values.businessDescription,
            postalAddress: valuesFromStepA.postalAddress,
            phoneNumber: valuesFromStepA.phoneNumber,
            numInvoices: (_values_numInvoices = values.numInvoices) !== null && _values_numInvoices !== void 0 ? _values_numInvoices : undefined,
            teamSize: (_values_teamSize = values.teamSize) !== null && _values_teamSize !== void 0 ? _values_teamSize : undefined
        };
        const payloadSignature = JSON.stringify(payload);
        if (hasCreatedBusiness && lastSubmittedSignature === payloadSignature) {
            onContinue();
            return;
        }
        setIsSubmitting(true);
        setSubmitFailed(false);
        try {
            if (hasCreatedBusiness) {
                await api.vendor.update({
                    name: payload.name,
                    description: payload.description,
                    postalAddress: payload.postalAddress,
                    numInvoices: payload.numInvoices,
                    teamSize: payload.teamSize
                });
            } else {
                const createResponse = await api.vendor.create({
                    name: payload.name,
                    description: payload.description,
                    numInvoices: payload.numInvoices,
                    teamSize: payload.teamSize
                });
                if (createResponse.authToken != null) {
                    onAuthTokenReceived === null || onAuthTokenReceived === void 0 ? void 0 : onAuthTokenReceived(createResponse.authToken);
                }
                const updateDetailsResponse = await api.vendor.updateDetails({
                    postalAddress: payload.postalAddress
                });
                if (updateDetailsResponse.authToken != null) {
                    onAuthTokenReceived === null || onAuthTokenReceived === void 0 ? void 0 : onAuthTokenReceived(updateDetailsResponse.authToken);
                }
                onBusinessCreated();
            }
            await api.auth.editUserDetails({
                phone: payload.phoneNumber
            });
            onSubmitted(payloadSignature);
            onContinue();
        } catch (error) {
            // TODO(ANC-12230): surface richer error UX once design is finalized; for now, show inline error and unblock retry.
            // eslint-disable-next-line no-console
            console.error('[BusinessDetailsBStep] vendor creation failed', error);
            setSubmitFailed(true);
        } finally{
            setIsSubmitting(false);
        }
    }, [
        api,
        errors,
        hasCreatedBusiness,
        isSubmitting,
        lastSubmittedSignature,
        onAuthTokenReceived,
        onBusinessCreated,
        onContinue,
        onSubmitted,
        values.businessDescription,
        values.numInvoices,
        values.teamSize,
        valuesFromStepA
    ]);
    useImperativeHandle(submitRef, ()=>({
            submit: handleSubmit
        }), [
        handleSubmit
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        "data-testid": testIds.BusinessDetailsBStepTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.section,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        className: styles.sectionLabel,
                        children: t.businessDescriptionSectionTitle()
                    }),
                    /*#__PURE__*/ _jsx(AnimatedTextField, {
                        value: values.businessDescription,
                        onChange: (e)=>onChange(_object_spread_props(_object_spread({}, values), {
                                businessDescription: e.target.value
                            })),
                        suggestions: businessDescriptionSuggestions,
                        staticPrefix: t.businessDescriptionStaticPrefix(),
                        collapseHelperText: true,
                        showHelperTextOnFocusOnly: true,
                        helperText: showErrors && errors.businessDescription != null ? errors.businessDescription : t.businessDescriptionHelperText(),
                        error: showErrors && errors.businessDescription != null,
                        testId: testIds.BusinessDetailsBStepTestIds.BusinessDescriptionField(),
                        bi: {
                            name: 'business_description'
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.section,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        className: styles.sectionLabel,
                        children: t.numInvoicesSectionTitle()
                    }),
                    /*#__PURE__*/ _jsx(SegmentControl, {
                        options: numInvoicesOptions,
                        value: (_values_numInvoices = values.numInvoices) !== null && _values_numInvoices !== void 0 ? _values_numInvoices : '',
                        onChange: (numInvoices)=>{
                            if (numInvoices !== '') onChange(_object_spread_props(_object_spread({}, values), {
                                numInvoices
                            }));
                        },
                        testId: testIds.BusinessDetailsBStepTestIds.NumInvoicesSelect()
                    }),
                    showErrors && errors.numInvoices != null && /*#__PURE__*/ _jsx(Typography, {
                        skin: "error",
                        children: errors.numInvoices
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.section,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        className: styles.sectionLabel,
                        children: t.teamSizeTitle()
                    }),
                    /*#__PURE__*/ _jsx(SegmentControl, {
                        options: teamSizeOptions,
                        value: (_values_teamSize = values.teamSize) !== null && _values_teamSize !== void 0 ? _values_teamSize : '',
                        onChange: (teamSize)=>{
                            if (teamSize !== '') onChange(_object_spread_props(_object_spread({}, values), {
                                teamSize
                            }));
                        },
                        testId: testIds.BusinessDetailsBStepTestIds.TeamSizeChip()
                    }),
                    showErrors && errors.teamSize != null && /*#__PURE__*/ _jsx(Typography, {
                        skin: "error",
                        children: errors.teamSize
                    })
                ]
            }),
            submitFailed && /*#__PURE__*/ _jsx(Typography, {
                skin: "error",
                testId: testIds.BusinessDetailsBStepTestIds.SubmitError(),
                children: t.submitError()
            })
        ]
    });
};
